import { useCallback, useEffect, useMemo, useState } from "react";

import Combobox from "../../../shared/ui/Field/Combobox";
import Spinner from "../../../shared/ui/Loading/Spinner";
import InlineError from "../../../shared/ui/Field/InlineError";
import { useGeoRegions } from "../hooks/useGeoRegions";

function regionToOption(region, formatOptionLabel) {
  if (!region || !region.code) return null;
  return {
    id: region.code,
    label: formatOptionLabel ? formatOptionLabel(region) : region.name,
    raw: region,
  };
}

function GeoRegionSelect({
  id,
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  allowEmpty = false,
  emptyOptionLabel = "Усі регіони",
  formatOptionLabel,
  placeholder = "Оберіть область або місто",
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
}) {
  const { regions, loading, error: loadError, reload } = useGeoRegions();
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);

  const resolvedValue = useMemo(() => {
    if (!value || !value.code) return null;
    return regions.find((region) => region.code === value.code) || value;
  }, [regions, value]);

  useEffect(() => {
    if (!allowEmpty || loading || !value?.code || regions.length === 0) {
      return;
    }
    const exists = regions.some((region) => region.code === value.code);
    if (!exists) {
      onChange(null);
    }
  }, [allowEmpty, loading, onChange, regions, value]);

  const committedLabel = resolvedValue
    ? (formatOptionLabel
      ? formatOptionLabel(resolvedValue)
      : resolvedValue.name)
    : "";

  // Search text is temporary UI state only. The committed Region label
  // is restored whenever search mode exits without a new selection.
  const displayValue = isSearching ? query : committedLabel;

  const committedOptionId = resolvedValue?.code
    || (allowEmpty ? "__all-regions__" : undefined);

  const filteredOptions = useMemo(() => {
    const needle = String(query || "").trim().toLowerCase();
    const mapped = regions
      .map((region) => regionToOption(region, formatOptionLabel))
      .filter(Boolean)
      .filter((option) => {
        if (!needle) return true;
        return (
          option.label.toLowerCase().includes(needle)
          || String(option.raw.code).toLowerCase().includes(needle)
        );
      });
    if (allowEmpty && !needle) {
      return [
        { id: "__all-regions__", label: emptyOptionLabel, raw: null },
        ...mapped,
      ];
    }
    return mapped;
  }, [allowEmpty, emptyOptionLabel, formatOptionLabel, query, regions]);

  const handleChange = useCallback((event) => {
    setIsSearching(true);
    setQuery(event.target.value);
  }, []);

  const handleSelect = useCallback(
    (option) => {
      setQuery("");
      setIsSearching(false);
      onChange(option?.raw || null);
    },
    [onChange],
  );

  const handleOpen = useCallback(() => {
    if (resolvedValue && !isSearching) {
      setIsSearching(true);
      setQuery("");
    }
  }, [isSearching, resolvedValue]);

  const handleClose = useCallback(() => {
    setQuery("");
    setIsSearching(false);
  }, []);

  if (loading) {
    return (
      <div className="geo-region-select geo-region-select--loading">
        <Spinner size="sm" label="Завантаження областей" />
        <span className="geo-region-select__state-text">Завантаження областей…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="geo-region-select geo-region-select--error">
        <InlineError>
          {typeof loadError === "string" ? loadError : "Не вдалося завантажити регіони"}
        </InlineError>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary geo-region-select__retry"
          onClick={() => reload()}
        >
          Спробувати знову
        </button>
      </div>
    );
  }

  if (regions.length === 0) {
    return (
      <div className="geo-region-select">
        <Combobox
          id={id}
          name={id}
          value=""
          onChange={() => {}}
          options={
            allowEmpty
              ? [{ id: "__all-regions__", label: emptyOptionLabel, raw: null }]
              : []
          }
          disabled
          placeholder={allowEmpty ? emptyOptionLabel : placeholder}
          aria-label={ariaLabel}
          emptyHint={allowEmpty ? emptyOptionLabel : "Немає доступних регіонів."}
        />
      </div>
    );
  }

  return (
    <div className="geo-region-select">
      <Combobox
        id={id}
        name={id}
        value={displayValue}
        onChange={handleChange}
        options={filteredOptions}
        disabled={disabled}
        required={Boolean(required && !resolvedValue)}
        placeholder={placeholder}
        autoComplete="off"
        aria-describedby={ariaDescribedBy}
        aria-invalid={ariaInvalid}
        aria-label={ariaLabel}
        emptyHint={placeholder}
        emptyResultsLabel={
          String(query || "").trim()
            ? `За запитом «${String(query).trim()}» нічого не знайдено.`
            : undefined
        }
        onSelect={handleSelect}
        onOpen={handleOpen}
        onClose={handleClose}
        selectedId={committedOptionId}
      />
      {error ? (
        <small className="form-hint text-danger">{error}</small>
      ) : null}
    </div>
  );
}

export default GeoRegionSelect;
